import React, { memo, useCallback } from 'react';
import { has, isEmpty, map, pickBy } from 'lodash';
import classnames from 'classnames';
import cx from 'classnames';
import { ReactComponent as Expand } from 'js/components/elements/treeview/assets/expandMoreFilled.svg';
import { ReactComponent as PlusIcon } from 'new/components/icons/fi-rr-plus.svg';
import { CircularProgress } from '@mui/material';
import { ReactComponent as PrimarykeyIcon } from 'new/components/icons/primary-key.svg';
import { CONNECTION_CATEGORY_NOSQL } from 'js/constants/connectionCategories';
import { Button } from 'new/elements/Button';
import Typography from 'new/elements/Typography';
import { Stack } from 'new/elements/Stack';
import { useDispatch } from 'react-redux';
import { probeGetSample } from 'js/actions';
import { filter } from 'js/components/elements/treeview/TreeView';
import { Virtuoso } from 'react-virtuoso';
import PreviewButton from './PreviewButton';
import { TableColumnsSkeleton } from 'new/components/database/TableColumnsSkeleton';

function TableColumns({ columns }) {
  const hasDefaultValue = columns?.some((c) => has(c, 'defaultValue')) ?? false;

  return (
    <Stack paddingLeft="8px">
      <table className="columns-table">
        <thead>
          <tr>
            <th className="columns-table-col-15">Column</th>
            <th className="columns-table-col-15">Type</th>
            <th className="columns-table-col-15">Primary</th>
            {hasDefaultValue && <th className="columns-table-col-15">Default Value</th>}
          </tr>
        </thead>
        <tbody>
          {columns?.map((row) => (
            <tr key={row.name}>
              <td className="columns-table-col-15">{row.name}</td>
              <td className="columns-table-col-15 type-color">{row.type}</td>
              <td className="columns-table-col-15">
                {row.primaryKey ? <PrimarykeyIcon width={16} height={16} /> : null}
              </td>
              {hasDefaultValue && (
                <td
                  className={classnames('columns-table-col-15', {
                    'missing-default-value': !has(row, 'defaultValue'),
                  })}
                >
                  {has(row, 'defaultValue') ? String(row.defaultValue) : '(none)'}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </Stack>
  );
}

class TableItem extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isSelecting: false,
      isExpanding: false,
      isWaitingForData: false,
    };

    this._onExpand = this._onExpand.bind(this);
    this._onSelect = this._onSelect.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { t, probeDataFlat } = this.props;

    const probeJustScanned =
      prevProps.probeDataFlat !== probeDataFlat &&
      probeDataFlat[t.id]?.scanned &&
      !prevProps.probeDataFlat[t.id]?.scanned;

    // When the probe completes without an active expand gesture, flag that we're
    // waiting for the table data to propagate through DatabaseItem's local state.
    if (probeJustScanned && !this.state.isExpanding) {
      this.setState({ isWaitingForData: true });
    } else if (
      (this.state.isSelecting || this.state.isExpanding || this.state.isWaitingForData) &&
      probeDataFlat[t.id]?.scanned &&
      t !== prevProps.t
    ) {
      // t prop updated (columns arrived) — safe to drop all loading flags
      this.setState({ isSelecting: false, isExpanding: false, isWaitingForData: false });
    }
  }

  renderExpandedContent() {
    const { t, probeDataFlat } = this.props;
    const { isExpanding, isWaitingForData } = this.state;
    // Use probeDataFlat[t.id]?.scanned rather than isProbingTree so the skeleton
    // persists across gaps between chained probes (e.g. root → db → table).
    const tableScanned = probeDataFlat[t.id]?.scanned;
    const isLoading = isExpanding || isWaitingForData || !tableScanned;
    const hasColumns = !isEmpty(t.columns);

    if (isLoading) {
      return (
        <Stack paddingLeft="8px">
          <TableColumnsSkeleton />
        </Stack>
      );
    }

    if (!hasColumns) {
      return <div className="empty-database-message">This table does not have a schema.</div>;
    }

    return <TableColumns columns={t.columns} />;
  }

  onClickPreviewSamples = (e) => {
    e.stopPropagation();

    const { tableName } = e.currentTarget.dataset;

    this.props.handleShowPreview(true, tableName);
    this.props.onShowSamples(this.props.database, this.props.t);
  };

  render() {
    const { t, selected, expanded, shouldShowExpandedView, credentialId } = this.props;
    const { isSelecting, isExpanding } = this.state;
    const isLoading = isSelecting || isExpanding;
    const shouldShowExpandedContent = expanded && shouldShowExpandedView;

    return (
      <li
        className={classnames(
          'table-node',
          {
            expanded: expanded,
            selected: selected,
          },
          'table-node-database',
        )}
      >
        <div
          data-testid={`table-node-${t.id}`}
          className="table-row-content table-row-content-table"
          onClick={this._onSelect}
        >
          {shouldShowExpandedView && (
            <>
              {isLoading ? (
                <CircularProgress
                  size={16}
                  thickness={4}
                  data-testid="table-loader"
                  style={{ marginRight: 10 }}
                  color="primary"
                />
              ) : (
                <div
                  className="expand-icon-button"
                  onClick={(e) => shouldShowExpandedView && this._onExpand(e)}
                >
                  <Expand
                    className={cx('expand', {
                      ['expand-opened']: expanded,
                      ['expand-closed']: !expanded,
                    })}
                  />
                </div>
              )}
            </>
          )}
          <div className="table-row-detail" data-testid={`table-node-detail-${t.id}`}>
            <div key={t.id} className="table-item">
              <div className="table-node-detail-icon" />
              <Typography variant="bodyDefault">{t.name}</Typography>
              {shouldShowExpandedView && (
                <Stack
                  className="preview-samples"
                  direction="row"
                  justifyContent="flex-end"
                  width="100%"
                >
                  <PreviewButton data-table-name={t.name} onClick={this.onClickPreviewSamples} />
                </Stack>
              )}
            </div>
          </div>
        </div>
        {shouldShowExpandedContent && this.renderExpandedContent()}
      </li>
    );
  }

  _onExpand(e) {
    const { t, probeDataFlat, expanded, expandTable } = this.props;

    e.stopPropagation();

    if (expanded) {
      expandTable(null);
    } else {
      const isLoaded = probeDataFlat[t.id] && probeDataFlat[t.id].scanned;

      if (isLoaded) {
        expandTable(t.id);
      } else {
        this.setState({ isExpanding: true }, () => {
          expandTable(t.id);
        });
      }
    }
  }

  _onSelect(e) {
    const { t, probeDataFlat, openTableEditor } = this.props;

    e.stopPropagation();

    if (probeDataFlat[t.id] && probeDataFlat[t.id].scanned) {
      openTableEditor(t.id);
    } else {
      this.setState(
        {
          isSelecting: true,
          isExpanding: true,
        },
        () => {
          openTableEditor(t.id);
        },
      );
    }
  }
}

function TableList({
  database,
  tables,
  search,
  openTableEditor,
  expandTable,
  credentialId,
  expandedTableId,
  selectedTable,
  isNewTable,
  probeDataFlat,
  shouldShowExpandedView,
  handleShowPreview,
  connectionCategory,
  canCreateTable = true,
  isProbingTree = false,
}) {
  const dispatch = useDispatch();
  let newTableClass = classnames('add-table-button table-node', {
    selected: isNewTable,
  });

  let createNewText = `New Table`;
  let emptyDatabaseMessage = `This database does not have any tables.`;
  if (connectionCategory && connectionCategory === CONNECTION_CATEGORY_NOSQL) {
    createNewText = `New Collection`;
    emptyDatabaseMessage = `This database does not have any collections.`;
  }

  const onShowSamples = useCallback(
    (database, table) => {
      dispatch(
        probeGetSample(credentialId, 0, {
          db_query_mode: 'Default',
          database: database.name,
          table: table.name,
        }),
      );
    },
    [credentialId],
  );

  const filteredTables = search ? pickBy(tables, filter(search)) : tables;

  const itemRenderer = (index, t) => {
    const expanded = t.id === expandedTableId;
    const selected = t.id === selectedTable.id;

    return (
      <TableItem
        key={`${t.id}_${index}`}
        t={t}
        database={database}
        openTableEditor={openTableEditor}
        onShowSamples={onShowSamples}
        expandTable={expandTable}
        selected={selected}
        expanded={expanded}
        probeDataFlat={probeDataFlat}
        isProbingTree={isProbingTree}
        shouldShowExpandedView={shouldShowExpandedView}
        handleShowPreview={handleShowPreview}
        credentialId={credentialId}
      />
    );
  };

  return (
    <ul className="table-list">
      {canCreateTable ? (
        <li className={newTableClass}>
          <Button
            data-testid="create-new-table"
            size="small"
            variant="outlined"
            color={isNewTable ? 'secondary' : 'basic'}
            onClick={(e) => {
              e.stopPropagation();
              openTableEditor();
            }}
            startIcon={<PlusIcon />}
          >
            {createNewText}
          </Button>
        </li>
      ) : null}

      {isEmpty(filteredTables) && (
        <div className="empty-database-message">{emptyDatabaseMessage}</div>
      )}

      {filteredTables.length > 500 ? (
        <Stack flexGrow="1">
          <Virtuoso
            style={{ height: '400px' }}
            itemContent={(index) => itemRenderer(index, filteredTables[index])}
            overscan={200}
            totalCount={filteredTables.length}
          />
        </Stack>
      ) : (
        map(filteredTables, (t, ix) => itemRenderer(ix, t))
      )}
    </ul>
  );
}

export default memo(TableList);
